import { CursorPage } from "@hlcr/core";
import { Paper, Table, TableContainer } from "@material-ui/core";
import TableBody from "@material-ui/core/TableBody";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { fetchReportedBugs } from "bugBounty/bugBounty.actions";
import ReportBugsTableContent from "bugBounty/components/ReportBugsTableContent";
import ReportBugsTableEmpty from "bugBounty/components/ReportBugsTableEmpty";
import ReportBugsTableHead from "bugBounty/components/ReportBugsTableHead";
import { ReportBugsTableLoading } from "bugBounty/components/ReportBugsTableLoading";
import ReportedBugTableFilterInput from "bugBounty/components/ReportBugTableFilterInput";
import TablePaginationControls from "bugBounty/components/TablePaginationControls";
import { BugBountyReportSummary } from "bugBounty/models/BugBountyReport";
// eslint-disable-next-line import/no-named-as-default -- Suppress warning from old JSX import

interface ReportedBugsTableProps {
	eventId?: number;
}

export const ReportedBugsTable = (props: ReportedBugsTableProps) => {
	const dispatch = useDispatch();

	// local states
	// data
	const [ loading, setLoading ] = useState<boolean>(false);
	const [ foundBugs, setFoundBugs ] = useState<BugBountyReportSummary[]>([]);
	const [ filterQuery, setFilterQuery ] = useState<string>("");

	// pagination
	const [ nextPageToken, setNextPageToken ] = useState("");
	const [ currentPageToken, setCurrentPageToken ] = useState("");
	const [ prevPageTokens, setPrevPageTokens ] = useState<string[]>([]);
	const [ pageSize, setPageSize ] = useState(10);


	const handleOnReportedBugsReceived = (reportedBugsPage: CursorPage<BugBountyReportSummary>) => {
		setFoundBugs(reportedBugsPage.content);
		setNextPageToken(reportedBugsPage.nextPageToken);
		setLoading(false);
	};

	const reloadFoundBugs = (token: string) => {
		setLoading(true);
		dispatch(fetchReportedBugs(token, pageSize, props.eventId, filterQuery, "", [], handleOnReportedBugsReceived));
	};

	useEffect(() => {
		reloadFoundBugs(currentPageToken);
	}, [ currentPageToken, pageSize ]);

	useEffect(() => {
		const delayedSearch = setTimeout(() => {
			setNextPageToken("");
			setPrevPageTokens([]);
			reloadFoundBugs("");
		}, 500);
		return () => clearTimeout(delayedSearch);
	}, [ filterQuery ]);

	const history = useHistory();

	const handleOpenBug = (eventId: number, bugId: string) => {
		history.push(`/bugbounty/${eventId}/bug/${bugId}`);
	};

	const handleOpenEvent = (eventId: number) => {
		history.push(`/bugbounty/${eventId}`);
	};

	const onFilterInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.currentTarget;

		if (filterQuery !== value) {
			setFilterQuery(value);
		}
	};

	function goToNextPage() {
		setPrevPageTokens(prev => [ ...prev, currentPageToken ]);
		setCurrentPageToken(nextPageToken);
	}
	function goToPrevPage() {
		const lastToken = prevPageTokens[prevPageTokens.length - 1];
		setPrevPageTokens(prev => prev.slice(0, prevPageTokens.length - 1));
		setCurrentPageToken(lastToken);
	}

	const handlePageSizeChange = (newPageSize: number) => {
		setCurrentPageToken(""); // Reset to first page
		setPrevPageTokens([]);
		setPageSize(newPageSize);
	};

	const onRowClick = (bugBountyReport: BugBountyReportSummary, event: any) => {
		handleOpenBug(bugBountyReport.programId, bugBountyReport.id!);
	};

	const getPage= ()=> {
		return prevPageTokens.length;
	};

	const onCellClick = (bugBountyReport: BugBountyReportSummary, event: any) => {
		event.stopPropagation();
		handleOpenEvent(bugBountyReport.programId);
	};

	return (
		<>
			<ReportedBugTableFilterInput onFilterInputChange={onFilterInputChange} />
			<TableContainer component={Paper} style={{ height: 400 }}>
				<Table stickyHeader>
					<ReportBugsTableHead />
					<TableBody>
						{loading && (<ReportBugsTableLoading />)}
						{!loading && foundBugs.length === 0 && (<ReportBugsTableEmpty />)}
						{!loading && foundBugs.length > 0 && (
							<ReportBugsTableContent
								foundBugs={foundBugs}
								onRowClick={onRowClick}
								onCellClick={onCellClick}
								handleOpenBug={handleOpenBug}
							/>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<TablePaginationControls
				pageSize={pageSize}
				handlePageSizeChange={handlePageSizeChange}
				getPage={getPage}
				goToPrevPage={goToPrevPage}
				goToNextPage={goToNextPage}
				nextPageToken={nextPageToken}
			></TablePaginationControls>
		</>
	);
};
